import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, OnInit, signal, viewChildren } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { debounceTime, distinctUntilChanged } from 'rxjs'

import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTooltipModule } from '@angular/material/tooltip'

import { Api } from '../../shared/api'
import { Card } from '../../shared/card/card'
import { CardGroup } from '../../shared/card-group/card-group'
import { ExpandCollapseMenu } from '../../shared/expand-collapse-menu/expand-collapse-menu'
import { AttentionNeededStatus, Relationship, RelationshipGroup } from '../relationship-interface'
import { PageHeaderBar } from '../../shared/page-header-bar/page-header-bar'
import { Relationships } from '../relationships'
import { RelationshipCardContent } from '../relationship-card-content/relationship-card-content'
import { RelationshipUtilities } from '../relationship-utilities'
import { ResponsiveContent } from '../../shared/responsive-content/responsive-content'
import { ResponsiveContentElement } from '../../shared/responsive-content/responsive-content-element'
import { Row } from '../../shared/row/row'

@Component({
	selector: 'app-relationships-list',
	imports: [
		FormsModule, RouterLink,
		MatAutocompleteModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule,
		Card, CardGroup, ExpandCollapseMenu, PageHeaderBar, RelationshipCardContent, ResponsiveContent, ResponsiveContentElement, Row,
],
	templateUrl: './relationships-list.html',
	styleUrl: './relationships-list.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelationshipsList implements OnInit {
	// services
	private readonly api = inject(Api)
	private readonly relationshipsService = inject(Relationships)
	private readonly relationshipUtils = inject(RelationshipUtilities)
	private readonly snackBar = inject(MatSnackBar)

	private readonly cardGroups = viewChildren(CardGroup)

	// relationship data processing
	protected readonly groupedRelationships = signal<RelationshipGroup[]>([])
	private readonly ungroupedRelationships = computed(() => this.groupedRelationships().flatMap(({ relationships }) => relationships))
	private readonly relationshipNames = computed(() =>
		this.relationshipUtils.sortByFirstName(this.ungroupedRelationships()).map(({ fullName }) => fullName!) || []
	)
	protected readonly hasRelationships = computed(() => this.groupedRelationships().some(group => group.relationships.length))

	// search filter processing
	protected readonly searchValue = signal('')
	private readonly searchValueSub = toObservable(this.searchValue).pipe(
		takeUntilDestroyed(),
		debounceTime(300),
		distinctUntilChanged(),
	).subscribe(searchValue => this.applySearchFilter(searchValue))
	protected readonly filteredNames = signal<string[]>(this.relationshipNames())
	protected readonly filteredGroupedRelationships = linkedSignal(() => this.groupedRelationships() || [])

	// misc
	protected readonly showExpandAllGroupsButton = computed(() => !this.cardGroups().every(group => group.open()))
	protected readonly showCollapseAllGroupsButton = computed(() => !this.cardGroups().every(group => !group.open()))
	protected readonly isLoadingRelationships = signal(true)
	protected readonly highlightedCard = signal({ groupStatus: null, indexInGroup: null } as { groupStatus: AttentionNeededStatus|null, indexInGroup: number|null })

	ngOnInit(): void {
		this.api.getRelationshipsGroupedByStatus().subscribe({
			next: groupedRelationships => {
				this.groupedRelationships.set(groupedRelationships)
				this.isLoadingRelationships.set(false)
			},
			error: () => {
				this.isLoadingRelationships.set(false)
				this.snackBar.open('Failed to load relationships.', undefined)
			}
		})
	}

	/** Filter relationship groups and autocomplete suggestions by the current search value. */
	private applySearchFilter(searchValue: string): void {
		const lowerSearchValue = searchValue.toLowerCase().trim()
		let filteredNames = this.relationshipNames()
		let filteredGroups = this.groupedRelationships()

		if (lowerSearchValue) {
			// filter autocomplete suggested names
			filteredNames = this.relationshipNames().filter(name => name.toLowerCase().includes(lowerSearchValue))

			// filter grouped relationships
			filteredGroups = this.groupedRelationships().map<RelationshipGroup>(({ status, statusColor, relationships }) => ({
				status,
				statusColor,
				relationships: relationships.filter(({ fullName }) => fullName?.toLowerCase().includes(lowerSearchValue))
			}))
		}
		this.filteredNames.set(filteredNames)
		this.filteredGroupedRelationships.set(filteredGroups)
	}

	/** Expand or collapse every relationship group. */
	protected collapseOrExpandAllGroups(open: boolean): void {
		this.cardGroups().forEach(group => group.open.set(open))
	}

	/** Open the add-relationship flow and highlight the newly inserted relationship. */
	protected addRelationship(): void {
		this.relationshipsService.addRelationship(this.groupedRelationships())
			.subscribe(({ wasCancelled, groups, targetGroupStatus, targetRelationshipIndex }) => {
				if (wasCancelled) return
				this.groupedRelationships.set(groups)
				this.highlightedCard.set({ groupStatus: targetGroupStatus, indexInGroup: targetRelationshipIndex })
			})
	}

	/** Open the edit-relationship flow and update the filtered view with the saved result. */
	protected editRelationship(editTarget: Relationship): void {
		this.relationshipsService.editRelationship(editTarget, this.groupedRelationships())
			.subscribe(({ wasCancelled, groups, targetGroupStatus, targetRelationshipIndex }) => {
				if (wasCancelled) return
				this.groupedRelationships.set(groups)
				this.applySearchFilter(this.searchValue())
				this.highlightedCard.set({ groupStatus: targetGroupStatus, indexInGroup: targetRelationshipIndex })
			})
	}

	/** Delete a relationship after confirmation. */
	protected deleteRelationship(deleteTarget: Relationship): void {
		this.relationshipsService.deleteRelationship(deleteTarget).subscribe(targetDeleted => {
			if (targetDeleted) {
				this.groupedRelationships().forEach(group => {
					const deleteIndex = group.relationships.findIndex(({ _id }) => _id === deleteTarget._id)
					if (deleteIndex > -1) {
						group.relationships = [
							...group.relationships.slice(0, deleteIndex),
							...group.relationships.slice(deleteIndex + 1)
						]
					}
				})
			}
		})
	}

}

export default RelationshipsList
